/**
 * The Cards page's "which card to use" list, and the fee verdicts its tiles
 * share.
 *
 * The best-card engine is NOT here. It is `build_best_card_groups` in
 * `server/wallet_core/dashboard_logic.py`, which the API calls, so there is
 * one definition of which card
 * wins a category. This module fetches its output and turns numbers into the
 * strings a person reads.
 */

import { authedFetch } from "./api";
import { formatWhole } from "./money";

/**
 * A best-card table row. The keys are the human column headings the engine
 * builds, and reshaping them here would mean reimplementing the engine.
 */
export type BestCardRow = Record<string, string>;

export interface BestCardGroup {
  label: string;
  rows: BestCardRow[];
  /** Per row, the ids of the card(s) its "Best Card" names. More than one
   *  means a tie, and the name is a comma-joined list. Optional because
   *  Hosting can go live minutes before the API that sends it. (2026-09-10) */
  row_cards?: number[][];
}

/** The whole payload of GET /api/dashboard. */
export interface DashboardDto {
  today: string;
  best_card_groups: BestCardGroup[];
  /** null until the first monthly TPG valuation fetch has run. */
  valuation_date: string | null;
}

export async function fetchDashboard(): Promise<DashboardDto> {
  const res = await authedFetch("/api/dashboard");
  if (!res.ok) throw new Error(`Dashboard request failed (HTTP ${res.status})`);
  return (await res.json()) as DashboardDto;
}

/**
 * Which way is good.
 *
 * `net` is annual fee minus credits, so NEGATIVE means the credits cover the
 * fee and the card is ahead. Getting this backwards would colour every
 * profitable card red and every expensive one green -- a plausible-looking
 * page that inverts the one judgement it exists to support. Net `<= 0` is
 * the threshold on purpose, and zero counts as good deliberately: breaking
 * even is not a loss.
 */
export function netTone(net: number): "good" | "bad" {
  return net <= 0 ? "good" : "bad";
}

/**
 * The caption under the best-card table.
 *
 * A missing valuation date and a stale one look identical if the caption is
 * simply omitted, so the null case says explicitly that no fetch has run yet
 * rather than leaving the reader to assume the numbers are current.
 */
export function valuationCaption(date: string | null): string {
  return date
    ? `Est. ¢/$ based on TPG valuations as of ${date}.`
    : "Est. ¢/$ will appear after the next monthly valuation fetch.";
}

/**
 * A standing, as credits minus fees: "+$315" ahead, "−$63" behind.
 *
 * `net` is fee minus credits, so the sign flips here, because "is this fee
 * worth it" reads naturally as a gain. Which way is good still comes from
 * `netTone` alone.
 */
export function standingText(net: number): string {
  const v = -net;
  if (v === 0) return "$0";
  return `${v > 0 ? "+" : "−"}${formatWhole(Math.abs(v))}`;
}

const LOOKUP_OPEN_KEY = "wallet-portal.which-card-open";

/**
 * Whether the which-card list is unfolded. It stays the way it was left, so
 * someone who only wants their cards folds it once (2026-09-10). Per device:
 * localStorage, read defensively because it throws in some private modes, and
 * a page must still render with nothing stored. Open by default.
 */
export function readLookupOpen(): boolean {
  try {
    return localStorage.getItem(LOOKUP_OPEN_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeLookupOpen(open: boolean): void {
  try {
    localStorage.setItem(LOOKUP_OPEN_KEY, open ? "1" : "0");
  } catch {
    // Not remembering a fold is not worth an error on screen.
  }
}
